import React, { useState, useEffect } from 'react';
import { useLeadsData } from '@/hooks/useLeadsData';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Phone, 
  Mail, 
  Calendar, 
  User, 
  Building2, 
  Euro,
  TrendingUp,
  Plus,
  Save,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

interface LeadDetailModalProps {
  leadId: string;
  onClose: () => void;
}

const LEAD_STATUSES = [
  { value: 'nieuw', label: 'Nieuw' },
  { value: 'contact_opgenomen', label: 'Contact Opgenomen' },
  { value: 'interesse', label: 'Interesse' },
  { value: 'offerte_verstuurd', label: 'Offerte Verstuurd' },
  { value: 'gesloten', label: 'Gesloten' },
  { value: 'verloren', label: 'Verloren' }
];

const LeadDetailModal: React.FC<LeadDetailModalProps> = ({ leadId, onClose }) => {
  const { leads, updateLeadStatus, addLeadNote, updateLeadDetails } = useLeadsData();
  const [newNote, setNewNote] = useState('');
  const [nextActionDate, setNextActionDate] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const lead = leads.find(l => l.id === leadId);

  useEffect(() => {
    if (lead) {
      setNextActionDate(lead.next_action_date ? format(new Date(lead.next_action_date), 'yyyy-MM-dd') : '');
      setAssignedTo(lead.assigned_to || '');
    }
  }, [lead]);

  if (!lead) {
    return null;
  }

  const handleStatusChange = async (newStatus: string) => {
    await updateLeadStatus(leadId, newStatus as any);
  };

  const handleAddNote = async () => {
    if (newNote.trim()) {
      await addLeadNote(leadId, newNote);
      setNewNote('');
    }
  };

  const handleSaveDetails = async () => {
    await updateLeadDetails(leadId, {
      next_action_date: nextActionDate || null,
      assigned_to: assignedTo || null
    });
    setIsEditing(false);
  };

  const formatExpansionDetails = (details: Record<string, any>) => {
    const formatted: string[] = [];
    
    Object.entries(details).forEach(([planType, data]) => {
      switch (planType) {
        case 'location':
          formatted.push(`Locatie: ${data.location || 'N/A'}`);
          formatted.push(`Eigendom: ${data.ownership === 'buy' ? 'Kopen' : 'Huren'}`);
          break;
        case 'sustainability':
          formatted.push(`Certificeringen: ${data.certifications?.join(', ') || 'Geen'}`);
          break;
        case 'growth':
          formatted.push(`Groei: ${data.growthPercentage || 'N/A'}%`);
          break;
        case 'equipment':
          formatted.push(`Equipment: ${data.equipmentTypes?.join(', ') || 'Geen'}`);
          break;
      }
    });
    
    return formatted.join(' | ');
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">
                {lead.company_name || 'Geen bedrijfsnaam'}
              </DialogTitle>
              <DialogDescription>
                Lead Details & Management
              </DialogDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status & Quick Actions */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Status:</span>
                <Select value={lead.lead_status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map(status => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>Aangemaakt: {format(new Date(lead.created_at), 'dd MMM yyyy', { locale: nl })}</span>
                {lead.next_action_date && (
                  <span className={new Date(lead.next_action_date) < new Date() ? 'text-red-600 font-medium' : ''}>
                    Volgende actie: {format(new Date(lead.next_action_date), 'dd MMM yyyy', { locale: nl })}
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {lead.email && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`mailto:${lead.email}`, '_blank')}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </Button>
              )}
              <Button variant="outline" size="sm">
                <Phone className="h-4 w-4 mr-2" />
                Bellen
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lead Information */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Bedrijfsinformatie</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Bedrijfstype:</span>
                      <p>{lead.business_type}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Werknemers:</span>
                      <p>{lead.employee_count}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Email:</span>
                      <p>{lead.email || 'Niet beschikbaar'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Maandelijkse energiekosten:</span>
                      <p className="font-semibold text-green-600">€{lead.monthly_energy_costs.toLocaleString()}</p>
                    </div>
                  </div>
                  
                  {lead.energy_potential_score && (
                    <div className="pt-3 border-t">
                      <span className="font-medium text-gray-700">Energy Potential Score:</span>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={lead.energy_potential_score >= 70 ? "default" : "secondary"}>
                          {lead.energy_potential_score}%
                        </Badge>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{ width: `${lead.energy_potential_score}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Lead Management */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Lead Management</CardTitle>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setIsEditing(!isEditing)}
                    >
                      {isEditing ? 'Annuleren' : 'Bewerken'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Volgende actie datum:</label>
                    <Input
                      type="date"
                      value={nextActionDate}
                      onChange={(e) => setNextActionDate(e.target.value)}
                      disabled={!isEditing}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">Toegewezen aan:</label>
                    <Input
                      type="text"
                      placeholder="Naam medewerker"
                      value={assignedTo}
                      onChange={(e) => setAssignedTo(e.target.value)}
                      disabled={!isEditing}
                      className="mt-1"
                    />
                  </div>
                  
                  {isEditing && (
                    <Button onClick={handleSaveDetails} className="w-full">
                      <Save className="h-4 w-4 mr-2" />
                      Opslaan
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Notes & Activity */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Notities toevoegen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder="Voeg een notitie toe..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={3}
                  />
                  <Button onClick={handleAddNote} disabled={!newNote.trim()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Notitie toevoegen
                  </Button>
                </CardContent>
              </Card>

              {/* Existing Notes */}
              {lead.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Bestaande notities</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-gray-50 p-3 rounded text-sm">
                      {lead.notes}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Additional Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Aanvullende informatie</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  {lead.expansion_plans && lead.expansion_plans.length > 0 && (
                    <div>
                      <span className="font-medium">Uitbreidingsplannen:</span>
                      <p>{lead.expansion_plans.join(', ')}</p>
                    </div>
                  )}
                  
                  {lead.current_solutions && lead.current_solutions.length > 0 && (
                    <div>
                      <span className="font-medium">Huidige oplossingen:</span>
                      <p>{lead.current_solutions.join(', ')}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeadDetailModal;
